import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { GBP_ATTRIBUTES, getAttributeBySlug } from "@/lib/reference/attributes";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/gbp-attributes/$slug")({
  loader: ({ params }) => {
    const a = getAttributeBySlug(params.slug);
    if (!a) throw notFound();
    return a;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const url = `${SITE.url}/tools/gbp-attributes/${params.slug}`;
    const title = `${loaderData.name} — GBP Attribute`;
    const desc = loaderData.description.slice(0, 158);
    return {
      meta: [
        { title: `${title} | ${SITE.name}` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: loaderData.name,
            description: loaderData.description,
            url,
            inDefinedTermSet: `${SITE.url}/tools/gbp-attributes`,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE.url}/tools` },
              { "@type": "ListItem", position: 3, name: "GBP Attributes", item: `${SITE.url}/tools/gbp-attributes` },
              { "@type": "ListItem", position: 4, name: loaderData.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AttrDetail,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Not found</p>
        <h1 className="headline-display mt-3 text-4xl">Attribute not indexed yet</h1>
        <Link to="/tools/gbp-attributes" className="mt-6 inline-block underline">Back to attributes</Link>
      </main>
      <SiteFooter />
    </div>
  ),
});

function AttrDetail() {
  const params = Route.useParams();
  const a = Route.useLoaderData();
  const url = `${SITE.url}/tools/gbp-attributes/${params.slug}`;
  const related = GBP_ATTRIBUTES.filter((x) => x.slug !== a.slug && x.group === a.group).slice(0, 6);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools/gbp-attributes" className="hover:text-accent">GBP Attributes</Link></li>
            <li aria-hidden>/</li>
            <li>{a.name}</li>
          </ol>
        </nav>
        <header className="mt-6">
          <p className="eyebrow">{a.group}</p>
          <h1 className="headline-display mt-3 text-5xl">{a.name}</h1>
        </header>

        <p className="mt-8 text-xl leading-relaxed font-serif text-foreground/90">{a.description}</p>

        <section className="mt-10 grid gap-6 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-paper-soft p-5">
            <p className="eyebrow">Set by</p>
            <p className="mt-2 text-sm">{a.setBy}</p>
          </div>
          <div className="rounded-sm border border-border bg-paper-soft p-5">
            <p className="eyebrow">Appears for</p>
            <p className="mt-2 text-sm">{a.appearsFor.join(", ")}</p>
          </div>
        </section>

        <section className="mt-8 rounded-sm border border-border bg-paper-soft p-5">
          <p className="eyebrow">How it surfaces in Maps</p>
          <p className="mt-2 text-sm leading-relaxed">{a.surfacesAs}</p>
        </section>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-semibold">More {a.group} attributes</h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/tools/gbp-attributes/$slug"
                    params={{ slug: r.slug }}
                    className="block rounded-sm border border-border bg-paper-soft px-3 py-2 text-sm hover:border-foreground transition-colors"
                  >
                    {r.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-12 flex items-center gap-3 border-t border-b border-border py-5">
          <CiteThis title={`${a.name} — GBP Attribute`} url={url} author={`${SITE.name} Newsroom`} />
          <p className="text-xs text-muted-foreground">Free to cite with a link back.</p>
        </div>

        <NewsletterCTA />
      </article>
      <SiteFooter />
    </div>
  );
}
